import { createTheme } from '@mui/material/styles';
import { vortxColors, vortxTypography, getComponentOverrides } from './theme.js';

/**
 * Creates a Vortx-branded MUI theme
 *
 * @param {Object|string} options - Theme options object or mode string
 * @param {'light' | 'retro'} options.mode - Theme mode (default: 'light')
 * @returns {Object} MUI theme object
 *
 * @example
 * // Light mode (default)
 * const theme = createVortxTheme();
 *
 * @example
 * // Retro mode (string)
 * const theme = createVortxTheme('retro');
 *
 * @example
 * // Retro mode (object)
 * const theme = createVortxTheme({ mode: 'retro' });
 */
export const createVortxTheme = (options = {}) => {
  // Support both string mode ('retro') and object { mode: 'retro' }
  const mode = typeof options === 'string' ? options : (options.mode || 'light');
  const isRetro = mode === 'retro';

  // MUI only supports 'light' or 'dark' for palette.mode
  // Use 'dark' for retro mode so MUI generates proper dark theme
  const muiMode = isRetro ? 'dark' : 'light';

  return createTheme({
    palette: {
      mode: muiMode,
      primary: {
        main: vortxColors.gold,
        light: '#c99f6a',
        dark: '#9e7940',
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: vortxColors.teal,
        light: '#5a8d85',
        dark: '#2d4f4b',
        contrastText: '#FFFFFF',
      },
      background: {
        default: isRetro ? '#1a1a1a' : vortxColors.white,  // Cream background for light, dark for retro
        paper: isRetro ? '#242424' : '#FFFFFF',             // Card backgrounds
      },
      text: {
        primary: isRetro ? vortxColors.white : vortxColors.charcoal,
        secondary: isRetro ? vortxColors.gray : '#666666',  // Darker gray for better contrast on light backgrounds
      },
      error: {
        main: '#d32f2f',
      },
      warning: {
        main: '#ed6c02',
      },
      info: {
        main: vortxColors.teal,
      },
      success: {
        main: '#2e7d32',
      },
      divider: vortxColors.teal,
    },
    typography: {
      fontFamily: vortxTypography.body,
      h1: {
        fontFamily: vortxTypography.heading,
        fontWeight: 800,
        fontSize: '2.5rem',
        letterSpacing: '0.02em',
        textTransform: 'uppercase',
        color: vortxColors.gold,
      },
      h2: {
        fontFamily: vortxTypography.heading,
        fontWeight: 700,
        fontSize: '2rem',
        letterSpacing: '0.02em',
        color: vortxColors.gold,
      },
      h3: {
        fontFamily: vortxTypography.heading,
        fontWeight: 700,
        fontSize: '1.75rem',
        letterSpacing: '0.01em',
        color: vortxColors.gold,
      },
      h4: {
        fontFamily: vortxTypography.heading,
        fontWeight: 600,
        fontSize: '1.5rem',
        letterSpacing: '0.01em',
        color: isRetro ? vortxColors.gold : vortxColors.green,
      },
      h5: {
        fontFamily: vortxTypography.heading,
        fontWeight: 600,
        fontSize: '1.25rem',
        color: isRetro ? vortxColors.gold : vortxColors.green,
      },
      h6: {
        fontFamily: vortxTypography.heading,
        fontWeight: 600,
        fontSize: '1rem',
        color: isRetro ? vortxColors.white : vortxColors.charcoal,
      },
      body1: {
        fontFamily: vortxTypography.body,
        fontSize: '1rem',
        lineHeight: 1.6,
      },
      body2: {
        fontFamily: vortxTypography.body,
        fontSize: '0.875rem',
        lineHeight: 1.5,
      },
      button: {
        fontFamily: vortxTypography.body,
        fontWeight: 600,
        textTransform: 'none',
        letterSpacing: '0.02em',
      },
      caption: {
        fontFamily: vortxTypography.body,
        fontSize: '0.75rem',
        color: isRetro ? vortxColors.gray : vortxColors.stone,
      },
    },
    shape: {
      borderRadius: 8,
    },
    shadows: [
      'none',
      '0 2px 4px rgba(0, 0, 0, 0.1)',
      '0 4px 8px rgba(0, 0, 0, 0.12)',
      '0 6px 12px rgba(0, 0, 0, 0.15)',
      '0 8px 16px rgba(0, 0, 0, 0.18)',
      '0 10px 20px rgba(0, 0, 0, 0.2)',
      '0 12px 24px rgba(0, 0, 0, 0.22)',
      '0 14px 28px rgba(0, 0, 0, 0.25)',
      '0 16px 32px rgba(0, 0, 0, 0.27)',
      '0 18px 36px rgba(0, 0, 0, 0.3)',
      '0 20px 40px rgba(0, 0, 0, 0.32)',
      '0 22px 44px rgba(0, 0, 0, 0.35)',
      '0 24px 48px rgba(0, 0, 0, 0.37)',
      '0 4px 12px rgba(178, 140, 80, 0.3)',     // Gold shadow (index 13)
      '0 6px 20px rgba(178, 140, 80, 0.5)',     // Gold shadow hover (index 14)
      '0 8px 24px rgba(0, 0, 0, 0.4)',
      '0 10px 28px rgba(0, 0, 0, 0.42)',
      '0 12px 32px rgba(0, 0, 0, 0.45)',
      '0 14px 36px rgba(0, 0, 0, 0.47)',
      '0 16px 40px rgba(0, 0, 0, 0.5)',
      '0 18px 44px rgba(0, 0, 0, 0.52)',
      '0 20px 48px rgba(0, 0, 0, 0.55)',
      '0 22px 52px rgba(0, 0, 0, 0.57)',
      '0 24px 56px rgba(0, 0, 0, 0.6)',
      '0 26px 60px rgba(0, 0, 0, 0.62)',
    ],
    components: getComponentOverrides(mode),
  });
};
